import { validateCompositionRoute } from './compositionRoute.js';
import { PlatformError, type DocStorePort, type DocTxRunner } from '@djgg/core';
import { canonicalDigest, canonicalJson } from '@djgg/gameplay-kit';
import { assetGenJobDoc, buildJobDoc, versionDoc } from '../paths.js';
import { compositionMediaExecutionDigest, validateAcceptedCompositionV1 } from '../spec/gameSpec.js';
import type { AssetGenJob, BuildJob, CompositionProofBarrierV1, CreationVersion } from '../types.js';
import type { BuildClaimAuthority } from './buildJobs.js';
import { COMPOSITION_MEDIA_RELEASE_MISSED, compositionMediaReleaseMissed } from './measuredBuildWindow.js';

export interface CommitCompositionProofInput {
  jobId: string;
  claim: BuildClaimAuthority;
  checkpointRevision: number;
  candidateId: string;
}

/** The child set and proof come from accepted/retained server records, never
 * the caller's proposed list. Repeating a committed request is read-only;
 * changing its checkpoint, proof, authority or child set is a conflict.
 * Dispatch happens after this transaction. Queued children survive a crash
 * and their existing claim/reservation protocol owns dispatch idempotence. */
export async function commitCompositionProofBarrier(
  store: DocStorePort & Partial<DocTxRunner>,
  now: () => number,
  input: CommitCompositionProofInput,
): Promise<CompositionProofBarrierV1> {
  if (!store.runTransaction) throw new PlatformError('unavailable', 'Composition proof requires transactions');
  return store.runTransaction(async tx => {
    const job = await tx.get<BuildJob>(buildJobDoc(input.jobId));
    const run = job?.measuredExecution;
    if (!job || job.status !== 'claimed' || !input.claim.claimToken
      || job.workerId !== input.claim.workerId || job.claimToken !== input.claim.claimToken
      || job.leaseGeneration !== input.claim.leaseGeneration || (job.leaseExpiry ?? 0) <= now()
      || !run || ![4, 5].includes(run.recipe.revision) || now() >= run.deadlineAt - run.limits.finishReserveMs) {
      throw new PlatformError('conflict', 'Composition proof lost its active claim or deadline');
    }
    const version = await tx.get<CreationVersion>(versionDoc(job.creationId, job.version));
    if (!version || (!['queued', 'generating', 'gating'].includes(version.buildStatus)
      && !(version.buildStatus === 'publishing' && run.compositionBarrier))
      || version.pipelineSnapshot?.id !== 'measured-first-playable' || ![4, 5].includes(version.pipelineSnapshot.revision)
      || !version.executionPolicy?.composition) {
      throw new PlatformError('conflict', 'Composition proof version authority unavailable');
    }
    const authority = await validateAcceptedCompositionV1(version.executionPolicy.composition);
    const { mediaExecution } = await validateCompositionRoute(job, version, input.jobId);
    const mediaExecutionId = mediaExecution ? await compositionMediaExecutionDigest(mediaExecution) : undefined;
    // The release deadline governs RELEASING media. A successor request that
    // resumes after the barrier committed re-presents the same identity and is
    // checked read-only below; its media was released in time already.
    if (mediaExecution && compositionMediaReleaseMissed(run, mediaExecution, now())) {
      throw new PlatformError('unavailable', COMPOSITION_MEDIA_RELEASE_MISSED, { retryable: false });
    }
    const checkpoint = run.checkpoint;
    const candidate = checkpoint?.candidates[input.candidateId];
    const report = candidate?.report;
    const proof = report?.compositionProof as CreationVersion['gates']['compositionProof'];
    const checks = report?.checks as Record<string, { ran: boolean; passed: boolean | null; enforced: boolean }> | undefined;
    // Advisory failures remain recorded. They cannot impersonate required
    // evidence; absent/unknown required checks still block the barrier.
    const requiredPending = candidate?.remainingChecks.filter(name => checks?.[name]?.enforced !== false) ?? [];
    const basePassed = ['typecheck', 'tests', 'build'].every(name => (report?.[name] as { ok?: boolean } | undefined)?.ok === true);
    const nativePassed = checks?.['composition-native-loop']?.ran === true && checks['composition-native-loop'].passed === true;
    const proofKeys = ['authorityDigest', 'closureDigest', 'distDigest', 'nativeDigest', 'resolutionDigest', 'sourceDigest'];
    if (run.compositionDigest !== authority.digest || !checkpoint
      || checkpoint.revision !== input.checkpointRevision || checkpoint.currentCandidateId !== input.candidateId
      || checkpoint.bestCandidateId !== input.candidateId || !candidate?.runnable || !candidate.dist
      || requiredPending.length || !basePassed || !nativePassed || report?.ok !== true || !proof
      || proof.authorityDigest !== authority.digest || proof.closureDigest !== authority.closureDigest
      || canonicalJson(Object.keys(proof).sort()) !== canonicalJson(proofKeys) || !Object.values(proof).every(value => /^[a-f0-9]{64}$/.test(value))) {
      throw new PlatformError('conflict', 'Composition proof is incomplete or its checkpoint changed');
    }
    const mediaJobIds = [version.creationRun?.visualJobId, version.creationRun?.musicJobId]
      .filter((id): id is string => !!id).sort();
    if (new Set(mediaJobIds).size !== mediaJobIds.length) throw new PlatformError('conflict', 'Duplicate composition child identity');
    const proofDigest = await canonicalDigest(proof);
    const mediaBasisDigest = await canonicalDigest({ authorityDigest: authority.digest, sourceDigest: proof.sourceDigest,
      closureDigest: proof.closureDigest, mediaJobIds });
    const identity = { schemaVersion: 1 as const, authorityDigest: authority.digest,
      checkpointRevision: checkpoint.revision, candidateId: input.candidateId, proofDigest,
      structural: { candidateDigest: await canonicalDigest(candidate), candidate: structuredClone(candidate) },
      mediaBasisDigest, mediaJobIds };
    const retained = run.compositionBarrier;
    if (retained) {
      const { committedAt: _time, claimGeneration: _claim, ...priorIdentity } = retained;
      if (canonicalJson(priorIdentity) !== canonicalJson(identity)) throw new PlatformError('conflict', 'Composition proof barrier identity changed');
    }
    const children: Array<{ id: string; job: AssetGenJob }> = [];
    for (const id of mediaJobIds) {
      const child = await tx.get<AssetGenJob>(assetGenJobDoc(id));
      const prerequisite = child?.prerequisite;
      if (!child || child.creationId !== job.creationId || child.creationRun?.version !== job.version
        || prerequisite?.kind !== 'composition-proof' || prerequisite.authorityDigest !== authority.digest
        || prerequisite.runInputDigest !== authority.intent.inputDigest
        || child.measuredBudget?.buildJobId !== input.jobId || child.measuredBudget.acceptedAt !== run.acceptedAt
        || child.measuredBudget.deadlineAt !== run.deadlineAt
        || child.measuredBudget.role !== mediaExecution?.role
        || child.measuredBudget.authorityDigest !== (mediaExecution ? authority.digest : undefined)
        || child.measuredBudget.executionDigest !== mediaExecutionId
        || !!authority.media && (child.creationRun?.branch !== 'visual' || child.kind !== 'background'
          || child.name !== 'creation-plan'
          || canonicalJson([...(child.plan?.assetNames ?? [])].sort()) !== canonicalJson([...authority.media.slots].sort()))
        || (retained ? prerequisite.mediaBasisDigest !== mediaBasisDigest || prerequisite.releasedAt !== retained.committedAt
          : child.status !== 'awaiting-core' || prerequisite.mediaBasisDigest !== null || prerequisite.releasedAt !== null)) {
        throw new PlatformError('conflict', 'Composition media child has different authority or release state');
      }
      children.push({ id, job: child });
    }
    if (retained) return structuredClone(retained);
    const barrier: CompositionProofBarrierV1 = { ...identity, committedAt: now(), claimGeneration: job.leaseGeneration! };
    await tx.update(buildJobDoc(input.jobId), { measuredExecution: { ...run,
      ledgerRevision: (run.ledgerRevision ?? 0) + 1, compositionBarrier: barrier } });
    for (const { id, job: child } of children) {
      await tx.update(assetGenJobDoc(id), { status: 'queued', prerequisite: { ...child.prerequisite!,
        mediaBasisDigest, releasedAt: barrier.committedAt } });
    }
    return barrier;
  });
}
